import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import ConfirmationModal from '../components/ui/ConfirmationModal';
import { testConnection, connectDatabase, deleteDatabase, updateDatabase } from '../services/api';
import { Trash2, Plus, Search, Database } from 'lucide-react';
import { formatLocalDate } from '../utils/timezone';
import { WriteGuard } from '../components/auth/WriteGuard';

export const DatabaseConnection = () => {
    const { t } = useTranslation(['page-database-connection', 'common']);
    const { databases, refreshDatabases } = useDatabase();
    const { showToast } = useToast();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDb, setSelectedDb] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        db_type: 'postgresql',
        connection_string: '',
        description: '',
        tunnel_config: null, // Changed from object to null to match DB schema
    });
    // Frontend-only: input mode toggle ('string' | 'fields') + per-field values.
    const [inputMode, setInputMode] = useState('string');
    const [connFields, setConnFields] = useState({
        host: 'localhost',
        port: '',
        username: '',
        password: '',
        database: '',
    });
    const [testing, setTesting] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [message, setMessage] = useState(null);
    const [deleteModalInfo, setDeleteModalInfo] = useState({ isOpen: false, id: null });
    // Editable name/description for the selected (existing) connection.
    const [metaDraft, setMetaDraft] = useState({ name: '', description: '' });
    const [savingMeta, setSavingMeta] = useState(false);

    // Seed the edit draft whenever a different connection is selected.
    useEffect(() => {
        if (selectedDb) {
            setMetaDraft({
                name: selectedDb.name || '',
                description: selectedDb.description || '',
            });
        }
    }, [selectedDb?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const metaDirty =
        !!selectedDb &&
        (metaDraft.name.trim() !== (selectedDb.name || '').trim() ||
            (metaDraft.description || '').trim() !== (selectedDb.description || '').trim());

    const handleSaveMeta = async () => {
        if (!selectedDb) return;
        const name = metaDraft.name.trim();
        if (!name) {
            showToast(t('page-database-connection:toast.nameEmpty'), 'error');
            return;
        }
        setSavingMeta(true);
        try {
            const res = await updateDatabase(selectedDb.id, {
                name,
                description: metaDraft.description,
            });
            const updated = res.data?.data || {};
            setSelectedDb((prev) => (prev ? { ...prev, ...updated } : prev));
            await refreshDatabases();
            showToast(t('page-database-connection:toast.connectionUpdated'), 'success');
        } catch (error) {
            showToast(error.response?.data?.detail || t('page-database-connection:toast.updateFailed'), 'error');
        } finally {
            setSavingMeta(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const next = { ...prev, [name]: value };
            // Changing db type in fields mode re-derives the connection string.
            if (name === 'db_type' && inputMode === 'fields') {
                next.connection_string = buildConnectionString(value, connFields);
            }
            return next;
        });
    };

    // Per-db-type URI scheme + default port for building a connection string from fields.
    const DB_META = {
        postgresql: { scheme: 'postgresql', port: '5432' },
        mysql: { scheme: 'mysql', port: '3306' },
        mongodb: { scheme: 'mongodb', port: '27017' },
        clickhouse: { scheme: 'clickhouse', port: '8123' },
        sqlserver: { scheme: 'mssql', port: '1433' },
        sqlite: { scheme: 'sqlite', port: '' },
    };

    const buildConnectionString = (dbType, fields) => {
        const meta = DB_META[dbType] || { scheme: dbType, port: '' };
        if (dbType === 'sqlite') {
            // SQLite has no host/user/port — the database is a file path.
            return fields.database ? `sqlite:///${fields.database}` : '';
        }
        const port = fields.port || meta.port;
        const auth = fields.username
            ? `${encodeURIComponent(fields.username)}${fields.password ? ':' + encodeURIComponent(fields.password) : ''}@`
            : '';
        const hostPort = `${fields.host || 'localhost'}${port ? ':' + port : ''}`;
        const db = fields.database ? `/${fields.database}` : '';
        return `${meta.scheme}://${auth}${hostPort}${db}`;
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setConnFields(prev => {
            const next = { ...prev, [name]: value };
            setFormData(fd => ({ ...fd, connection_string: buildConnectionString(fd.db_type, next) }));
            return next;
        });
    };

    const handleTunnelChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            tunnel_config: {
                ...prev.tunnel_config,
                [name]: type === 'checkbox' ? checked : value,
            },
        }));
    };

    const handleTestConnection = async () => {
        setTesting(true);
        setMessage(null);
        try {
            await testConnection(formData);
            setMessage({ type: 'success', text: t('page-database-connection:toast.testSuccess') });
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.detail || t('page-database-connection:toast.testFailed') });
        } finally {
            setTesting(false);
        }
    };

    const handleConnect = async () => {
        setConnecting(true);
        setMessage(null);
        try {
            await connectDatabase(formData);
            setMessage({ type: 'success', text: t('page-database-connection:toast.connectSuccess') });
            await refreshDatabases();
            // Reset form
            setFormData({
                name: '',
                db_type: 'postgresql',
                connection_string: '',
                description: '',
                tunnel_config: null,
            });
            setInputMode('string');
            setConnFields({ host: 'localhost', port: '', username: '', password: '', database: '' });
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.detail || t('page-database-connection:toast.connectFailed') });
        } finally {
            setConnecting(false);
        }
    };

    const handleDelete = async (id) => {
        setDeleteModalInfo({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        const { id } = deleteModalInfo;
        if (!id) return;

        try {
            await deleteDatabase(id);
            await refreshDatabases();
            if (selectedDb?.id === id) {
                setSelectedDb(null);
            }
            showToast(t('page-database-connection:toast.deleteSuccess'), 'success');
        } catch (error) {
            showToast(error.response?.data?.detail || t('page-database-connection:toast.deleteFailed'), 'error');
        } finally {
            setDeleteModalInfo({ isOpen: false, id: null });
        }
    };

    const filteredDatabases = databases.filter(db =>
        db.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="flex gap-theme">
            {/* Left Sidebar — match Schema Analysis (w-64 + flex gap only, no extra main margin) */}
            <div className="w-64 bg-white border-r border-gray-200 flex flex-col rounded-xl shadow-sm h-fit max-h-[calc(100vh-8rem)]">
                {/* Sidebar Header */}
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900 mb-1">{t('page-database-connection:sidebar.title')}</h2>
                    <p className="text-sm text-gray-500">{t('page-database-connection:sidebar.subtitle')}</p>
                </div>

                {/* Search */}
                <div className="p-4 border-b border-gray-200">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder={t('page-database-connection:search.placeholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                    </div>
                </div>

                {/* New Connection Button */}
                <WriteGuard>
                    <div className="p-4">
                        <button
                            onClick={() => {
                                setSelectedDb(null);
                                setFormData({
                                    name: '',
                                    db_type: 'postgresql',
                                    connection_string: '',
                                    description: '',
                                    tunnel_config: null,
                                });
                                setInputMode('string');
                                setConnFields({ host: 'localhost', port: '', username: '', password: '', database: '' });
                            }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
                        >
                            <Plus className="w-4 h-4" />
                            {t('page-database-connection:newConnection')}
                        </button>
                    </div>
                </WriteGuard>

                {/* Database List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {filteredDatabases.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-8">
                            {searchQuery ? t('page-database-connection:list.noMatching') : t('page-database-connection:list.noConnections')}
                        </p>
                    ) : (
                        filteredDatabases.map((db) => (
                            <div
                                key={db.id}
                                onClick={() => setSelectedDb(db)}
                                className={`p-3 rounded-lg border transition-all cursor-pointer group ${selectedDb?.id === db.id
                                    ? 'bg-primary-50 border-primary-300'
                                    : 'bg-white border-gray-200 hover:border-primary-200 hover:bg-gray-50'
                                    }`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${selectedDb?.id === db.id ? 'bg-primary-100' : 'bg-green-100'
                                            }`}>
                                            <Database className={`w-4 h-4 ${selectedDb?.id === db.id ? 'text-primary-600' : 'text-green-600'
                                                }`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="min-w-0">
                                                <span className="block font-semibold text-gray-900 text-sm truncate">
                                                    {db.name}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-0.5">{db.db_type || 'PostgreSQL'}</p>
                                            <p className="text-xs text-gray-400 mt-0.5 truncate">
                                                {t('page-database-connection:list.createdLabel', { date: formatLocalDate(db.created_at) })}
                                            </p>
                                        </div>
                                    </div>
                                    <WriteGuard>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(db.id);
                                            }}
                                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </WriteGuard>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Right Panel - Connection Form */}
            <div className="flex-1">
                <div className="max-w-3xl">
                    {/* Header */}
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-900 mb-1">
                            {t('page-database-connection:form.title')}
                        </h1>
                        <p className="text-sm text-gray-500">
                            {selectedDb ? t('page-database-connection:form.subtitleView') : t('page-database-connection:form.subtitleNew')}
                        </p>
                    </div>

                    {/* Connection Form */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
                        {/* Connection Information Section */}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900 mb-4">{t('page-database-connection:form.connectionInfo')}</h3>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('page-database-connection:form.name')}
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={selectedDb ? metaDraft.name : formData.name}
                                        onChange={selectedDb
                                            ? (e) => setMetaDraft((d) => ({ ...d, name: e.target.value }))
                                            : handleChange}
                                        placeholder={t('page-database-connection:form.namePlaceholder')}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('page-database-connection:form.databaseType')}
                                    </label>
                                    <select
                                        name="db_type"
                                        value={selectedDb ? selectedDb.db_type : formData.db_type}
                                        onChange={handleChange}
                                        disabled={!!selectedDb}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                                    >
                                        <option value="postgresql">PostgreSQL</option>
                                        <option value="mysql">MySQL</option>
                                        <option value="sqlite">SQLite</option>
                                        <option value="mongodb">MongoDB</option>
                                        <option value="clickhouse">ClickHouse</option>
                                        <option value="sqlserver">MSSQL</option>
                                    </select>
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            {t('page-database-connection:form.connection')}
                                        </label>
                                        {!selectedDb && (
                                            <div className="inline-flex rounded-lg border border-gray-300 p-0.5 bg-gray-50">
                                                <button
                                                    type="button"
                                                    onClick={() => setInputMode('string')}
                                                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${inputMode === 'string' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:text-gray-900'}`}
                                                >
                                                    {t('page-database-connection:form.connectionStringTab')}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setInputMode('fields')}
                                                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${inputMode === 'fields' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:text-gray-900'}`}
                                                >
                                                    {t('page-database-connection:form.separateFieldsTab')}
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {(selectedDb || inputMode === 'string') ? (
                                        <input
                                            type="text"
                                            name="connection_string"
                                            value={selectedDb ? selectedDb.connection_string : formData.connection_string}
                                            onChange={handleChange}
                                            disabled={!!selectedDb}
                                            placeholder={t('page-database-connection:form.connectionStringPlaceholder')}
                                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                                        />
                                    ) : formData.db_type === 'sqlite' ? (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">{t('page-database-connection:form.databaseFilePath')}</label>
                                                <input
                                                    type="text"
                                                    name="database"
                                                    value={connFields.database}
                                                    onChange={handleFieldChange}
                                                    placeholder={t('page-database-connection:form.databaseFilePathPlaceholder')}
                                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                />
                                            </div>
                                            <p className="text-xs text-gray-400 break-all">{formData.connection_string || t('page-database-connection:form.sqlitePreviewPlaceholder')}</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('page-database-connection:form.host')}</label>
                                                    <input
                                                        type="text"
                                                        name="host"
                                                        value={connFields.host}
                                                        onChange={handleFieldChange}
                                                        placeholder="localhost"
                                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('page-database-connection:form.port')}</label>
                                                    <input
                                                        type="text"
                                                        name="port"
                                                        value={connFields.port}
                                                        onChange={handleFieldChange}
                                                        placeholder={(DB_META[formData.db_type] || {}).port || 'port'}
                                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('page-database-connection:form.username')}</label>
                                                    <input
                                                        type="text"
                                                        name="username"
                                                        value={connFields.username}
                                                        onChange={handleFieldChange}
                                                        placeholder="user"
                                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('page-database-connection:form.password')}</label>
                                                    <input
                                                        type="password"
                                                        name="password"
                                                        value={connFields.password}
                                                        onChange={handleFieldChange}
                                                        placeholder="password"
                                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">{t('page-database-connection:form.databaseName')}</label>
                                                <input
                                                    type="text"
                                                    name="database"
                                                    value={connFields.database}
                                                    onChange={handleFieldChange}
                                                    placeholder="dbname"
                                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                />
                                            </div>
                                            <p className="text-xs text-gray-400 break-all">{formData.connection_string || t('page-database-connection:form.previewPlaceholder')}</p>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('page-database-connection:form.description')}
                                    </label>
                                    <textarea
                                        name="description"
                                        value={selectedDb ? metaDraft.description : formData.description}
                                        onChange={selectedDb
                                            ? (e) => setMetaDraft((d) => ({ ...d, description: e.target.value }))
                                            : handleChange}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                                        rows="3"
                                        placeholder={t('page-database-connection:form.descriptionPlaceholder')}
                                    />
                                </div>

                                {selectedDb && (
                                    <WriteGuard>
                                        <div className="flex justify-end pt-2">
                                            <button
                                                onClick={handleSaveMeta}
                                                disabled={savingMeta || !metaDirty}
                                                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {savingMeta ? t('page-database-connection:form.saving') : t('common:saveChanges')}
                                            </button>
                                        </div>
                                    </WriteGuard>
                                )}
                            </div>
                        </div>

                        {/* SSH Tunnel Configuration */}
                        {!selectedDb && (
                            <div className="border-t border-gray-200 pt-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-sm font-semibold text-gray-900">{t('page-database-connection:tunnel.title')}</h3>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="enabled"
                                            checked={formData.tunnel_config?.enabled || false}
                                            onChange={(e) => {
                                                const { checked } = e.target;
                                                setFormData(prev => ({
                                                    ...prev,
                                                    tunnel_config: checked ? { enabled: true, host: '', username: '', port: 22, private_key_path: '' } : null
                                                }));
                                            }}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                    </label>
                                </div>

                                {formData.tunnel_config?.enabled && (
                                    <div className="space-y-4 mt-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {t('page-database-connection:tunnel.sshHost')}
                                                </label>
                                                <input
                                                    type="text"
                                                    name="host"
                                                    value={formData.tunnel_config?.host || ''}
                                                    onChange={handleTunnelChange}
                                                    placeholder="ssh.example.com"
                                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {t('page-database-connection:tunnel.sshUsername')}
                                                </label>
                                                <input
                                                    type="text"
                                                    name="username"
                                                    value={formData.tunnel_config?.username || ''}
                                                    onChange={handleTunnelChange}
                                                    placeholder="ubuntu"
                                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {t('page-database-connection:tunnel.sshPort')}
                                                </label>
                                                <input
                                                    type="number"
                                                    name="port"
                                                    value={formData.tunnel_config?.port || 22}
                                                    onChange={handleTunnelChange}
                                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {t('page-database-connection:tunnel.privateKeyPath')}
                                                </label>
                                                <input
                                                    type="text"
                                                    name="private_key_path"
                                                    value={formData.tunnel_config?.private_key_path || ''}
                                                    onChange={handleTunnelChange}
                                                    placeholder="/path/to/key.pem"
                                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Message */}
                        {message && (
                            <div
                                className={`p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                                    }`}
                            >
                                {message.text}
                            </div>
                        )}

                        {/* Action Buttons */}
                        {!selectedDb && (
                            <WriteGuard>
                                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                    <h3 className="text-sm font-semibold text-gray-900">{t('page-database-connection:testConnection.title')}</h3>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={handleTestConnection}
                                            disabled={testing || !formData.connection_string}
                                            className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {testing ? t('page-database-connection:testConnection.testing') : t('page-database-connection:testConnection.button')}
                                        </button>
                                        <button
                                            onClick={handleConnect}
                                            disabled={connecting || !formData.connection_string}
                                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {connecting ? t('page-database-connection:testConnection.connecting') : t('page-database-connection:testConnection.connect')}
                                        </button>
                                    </div>
                                </div>
                            </WriteGuard>
                        )}
                    </div>
                </div>
            </div>

            {/* Confirmation Modal for Delete */}
            <ConfirmationModal
                isOpen={deleteModalInfo.isOpen}
                onClose={() => setDeleteModalInfo({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title={t('page-database-connection:deleteModal.title')}
                message={t('page-database-connection:deleteModal.message')}
                confirmText={t('common:delete')}
                cancelText={t('common:cancel')}
            />
        </div>
    );
};
